<template>
  <div class="contract-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="contract-container">
      <!-- Header Section -->
      <div class="contract-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-return" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-file-contract title-icon"></i>
              Novo Contrato
            </h1>
            <p class="page-subtitle">
              Crie contratos personalizados usando variáveis dinâmicas
            </p>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="contract-content">
        <div class="content-grid">
          <!-- Main Form Card -->
          <div class="form-section">
            <div class="info-card">
              <div class="card-header-modern">
                <h3 class="card-title">
                  <i class="fas fa-edit mr-2"></i>
                  Dados do Contrato
                </h3>
              </div>
              <div class="card-content">
                <!-- Destinatários -->
                <div class="form-group">
                  <LabelRequiredField label="Destinatários do contrato" />
                  <div class="toggle-container">
                    <label
                      class="toggle-option"
                      :class="{ 'toggle-option--active': all_residents }"
                    >
                      <input
                        type="radio"
                        :value="true"
                        v-model="all_residents"
                        name="resident-scope"
                        class="toggle-input"
                      />
                      <div class="toggle-icon">
                        <i class="fas fa-users"></i>
                      </div>
                      <div class="toggle-text">
                        <span class="toggle-title">Todos os residentes</span>
                        <span class="toggle-desc">Aplicar para todos</span>
                      </div>
                    </label>

                    <label
                      class="toggle-option"
                      :class="{ 'toggle-option--active': !all_residents }"
                    >
                      <input
                        type="radio"
                        :value="false"
                        v-model="all_residents"
                        name="resident-scope"
                        class="toggle-input"
                      />
                      <div class="toggle-icon">
                        <i class="fas fa-user-check"></i>
                      </div>
                      <div class="toggle-text">
                        <span class="toggle-title">Selecionar residentes</span>
                        <span class="toggle-desc">Escolher manualmente</span>
                      </div>
                    </label>
                  </div>
                </div>

                <!-- Seleção de Residentes -->
                <div v-if="!all_residents" class="form-group">
                  <div class="residents-selector">
                    <div class="selector-header">
                      <div class="search-box">
                        <i class="fas fa-search search-icon"></i>
                        <input
                          type="text"
                          v-model="searchQuery"
                          placeholder="Buscar por nome ou quarto..."
                          class="search-input"
                        />
                        <button
                          v-if="searchQuery"
                          @click="searchQuery = ''"
                          class="search-clear"
                          type="button"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                      </div>

                      <div class="selector-actions">
                        <button
                          type="button"
                          @click="selectAll"
                          class="action-link"
                          :disabled="filteredResidents.length === 0"
                        >
                          <i class="fas fa-check-double mr-1"></i>
                          Todos
                        </button>
                        <button
                          type="button"
                          @click="selectNone"
                          class="action-link action-link--danger"
                          :disabled="resident_ids.length === 0"
                        >
                          <i class="fas fa-times mr-1"></i>
                          Limpar
                        </button>
                      </div>
                    </div>

                    <div
                      class="selection-counter"
                      :class="{
                        'selection-counter--warning': resident_ids.length === 0,
                      }"
                    >
                      <i
                        :class="
                          resident_ids.length === 0
                            ? 'fas fa-exclamation-triangle'
                            : 'fas fa-check-circle'
                        "
                        class="mr-2"
                      ></i>
                      <span v-if="resident_ids.length === 0"
                        >Nenhum residente selecionado</span
                      >
                      <span v-else-if="resident_ids.length === 1"
                        >1 residente selecionado</span
                      >
                      <span v-else
                        >{{ resident_ids.length }} residentes selecionados</span
                      >
                    </div>

                    <div class="residents-list">
                      <div v-if="loadingResidents" class="residents-loading">
                        <i class="fas fa-spinner fa-spin mr-2"></i>
                        Carregando residentes...
                      </div>

                      <div
                        v-else-if="
                          filteredResidents.length === 0 && searchQuery
                        "
                        class="residents-empty"
                      >
                        <i class="fas fa-search mr-2"></i>
                        Nenhum residente encontrado
                      </div>

                      <div
                        v-else-if="residents.length === 0"
                        class="residents-empty"
                      >
                        <i class="fas fa-user-slash mr-2"></i>
                        Nenhum residente ativo
                      </div>

                      <label
                        v-else
                        v-for="resident in filteredResidents"
                        :key="resident.id"
                        class="resident-item"
                        :class="{
                          'resident-item--selected': isSelected(resident.id),
                        }"
                      >
                        <input
                          type="checkbox"
                          :value="resident.id"
                          v-model="resident_ids"
                          class="resident-checkbox"
                        />
                        <div class="resident-avatar">
                          <i class="fas fa-user"></i>
                        </div>
                        <div class="resident-info">
                          <span class="resident-name">{{ resident.name }}</span>
                          <span v-if="resident.room" class="resident-room">
                            <i class="fas fa-door-open mr-1"></i>
                            Quarto {{ resident.room }}
                          </span>
                        </div>
                        <div
                          v-if="isSelected(resident.id)"
                          class="resident-check"
                        >
                          <i class="fas fa-check"></i>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Nome do Contrato -->
                <div class="form-group">
                  <LabelRequiredField label="Nome do contrato" />
                  <input
                    type="text"
                    v-model="name"
                    placeholder="Ex: Contrato de Hospedagem Padrão"
                    class="form-input"
                    maxlength="100"
                  />
                  <div class="input-footer">
                    <span class="char-counter">{{ name.length }}/100</span>
                  </div>
                </div>

                <!-- Situação do Contrato -->
                <div class="form-group">
                  <LabelRequiredField label="Situação do contrato" />
                  <select v-model="active" class="form-control">
                    <option :value="1">ATIVO</option>
                    <option :value="0">INATIVO</option>
                  </select>
                </div>

                <!-- Texto do Contrato -->
                <div class="form-group">
                  <LabelRequiredField label="Texto do contrato" />
                  <textarea
                    id="contract-text"
                    v-model="text"
                    placeholder="Digite o conteúdo do contrato aqui. Use as variáveis ao lado para inserir dados automaticamente."
                    class="form-textarea"
                    maxlength="5000"
                  ></textarea>
                  <div class="input-footer">
                    <span
                      class="char-counter"
                      :class="{ 'char-counter--warning': text.length > 4500 }"
                    >
                      {{ text.length }}/5000
                    </span>
                    <span class="input-hint">
                      <i class="fas fa-lightbulb mr-1"></i>
                      Clique nas variáveis ao lado para inserir
                    </span>
                  </div>
                </div>

                <!-- Errors -->
                <div v-if="errors.length > 0" class="errors-section">
                  <ErrorsMessages :errors="errors" />
                </div>

                <!-- Actions -->
                <div class="form-actions">
                  <v-btn
                    :disabled="loading || !isFormValid"
                    class="btn-save"
                    @click="update()"
                  >
                    <i class="fas fa-save mr-2"></i>
                    {{ loading ? "Salvando..." : "Salvar Contrato" }}
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Variables Panel -->
          <div class="variables-section">
            <div class="info-card variables-card">
              <div class="card-header-modern card-header--variables">
                <h3 class="card-title">
                  <i class="fas fa-code mr-2"></i>
                  Variáveis Disponíveis
                </h3>
              </div>
              <div class="card-content">
                <p class="variables-hint">
                  <i class="fas fa-info-circle mr-1"></i>
                  Clique para inserir no contrato
                </p>
                <div class="variables-list">
                  <ContractVariableList @insert="insertVariable" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import LabelRequiredField from "../templates/Components/LabelRequiredField";
import ContractVariableList from "../templates/Contract/ContractVariableList";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import toast from "../lib/ToastNotification";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import api from "../services/api";
import routes from "../routes";

export default {
  name: "ContractStandardNew",

  components: {
    NavBar,
    AuthBySessionExpired,
    LabelRequiredField,
    ErrorsMessages,
    ContractVariableList,
  },

  data() {
    return {
      loading: false,
      loadingResidents: false,
      errors: [],
      residents: [],
      contract_id: "",
      resident_ids: [],
      all_residents: true,
      name: "",
      text: "",
      active: 1,
      searchQuery: "",
    };
  },

  async created() {
    const { id } = routes.history.current.params;

    this.contract_id = id;

    await this.getResidents();
    await this.get();
  },

  computed: {
    filteredResidents() {
      if (!this.searchQuery.trim()) {
        return this.residents;
      }
      const query = this.searchQuery.toLowerCase().trim();
      return this.residents.filter((resident) => {
        const nameMatch = resident.name?.toLowerCase().includes(query);
        const roomMatch = resident.room?.toString().includes(query);
        return nameMatch || roomMatch;
      });
    },

    isFormValid() {
      const hasName = this.name.trim().length > 0;
      const hasText = this.text.trim().length > 0;
      const hasResidents = this.all_residents || this.resident_ids.length > 0;
      return hasName && hasText && hasResidents;
    },
  },

  watch: {
    all_residents(newValue) {
      if (newValue) {
        this.resident_ids = [];
        this.searchQuery = "";
      }
    },
  },

  methods: {
    async get() {
      try {
        let { contract_id } = this;
        this.loading = true;
        const response = await api.get("/contract/standard", {
          params: { contract_id },
        });
        const { error, errors, contract } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          if (contract) {
            console.log(contract);

            this.name = contract.name;
            this.text = contract.text;
            this.all_residents = contract.all_residents;
            this.active = contract.active;

            if (contract.all_residents == 0) {
              this.resident_ids = contract.residents.map(
                (resident) => resident.id
              );
            }
          }
        }
      } catch (error) {
        console.error("Erro ao carregar contrato:", error);
      } finally {
        this.loading = false;
      }
    },

    async getResidents() {
      try {
        this.loadingResidents = true;
        const response = await api.get("/rest-home/active-residents");
        const { error, errors, residents } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.residents = residents || [];
        }
      } catch (error) {
        console.error("Erro ao carregar residentes:", error);
      } finally {
        this.loadingResidents = false;
      }
    },

    isSelected(id) {
      return this.resident_ids.includes(id);
    },

    selectAll() {
      const filteredIds = this.filteredResidents.map((r) => r.id);
      filteredIds.forEach((id) => {
        if (!this.resident_ids.includes(id)) {
          this.resident_ids.push(id);
        }
      });
    },

    selectNone() {
      this.resident_ids = [];
    },

    insertVariable(variable) {
      const textarea = document.getElementById("contract-text");
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = this.text.substring(0, start);
      const after = this.text.substring(end);

      this.text = before + variable + after;

      this.$nextTick(() => {
        textarea.focus();
        const newPosition = start + variable.length;
        textarea.setSelectionRange(newPosition, newPosition);
      });
    },

    async update() {
      this.errors = [];

      if (!this.isFormValid) {
        if (!this.all_residents && this.resident_ids.length === 0) {
          this.errors = ["Selecione pelo menos um residente"];
        } else {
          this.errors = ["Preencha todos os campos obrigatórios"];
        }
        return;
      }

      try {
        this.loading = true;

        const response = await api.put("/contract/standard", {
          contract_id: this.contract_id,
          all_residents: this.all_residents,
          resident_ids: this.all_residents ? [] : this.resident_ids,
          name: this.name.trim(),
          text: this.text.trim(),
          active: this.active,
        });

        const { error, errors, message } = response.data;

        if (!error) {
          toast(message, "success");
          this.handleToBack();
        } else {
          this.errors = errors || ["Erro ao salvar contrato"];
        }
      } catch (error) {
        console.error("Erro ao criar contrato:", error);
        this.errors = ["Erro de conexão. Tente novamente."];
      } finally {
        this.loading = false;
      }
    },

    handleToBack() {
      routes.go(-1);
    },
  },
};
</script>

<style scoped>
/* ===========================================
   LAYOUT PRINCIPAL
   Seguindo padrão do Dashboard e Account
   =========================================== */

.contract-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.contract-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ===========================================
   HEADER SECTION
   =========================================== */

.contract-header {
  padding: 24px 0;
  margin-bottom: 24px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

/* ===========================================
   CONTENT GRID
   =========================================== */

.contract-content {
  margin-bottom: 32px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 24px;
  align-items: start;
}

/* ===========================================
   INFO CARD (padrão Account.vue)
   =========================================== */

.info-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.card-header-modern {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.card-header--variables {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  border-bottom: 1px solid #f59e0b;
}

.card-header--variables .card-title {
  color: #92400e;
}

.card-header--variables .card-title i {
  color: #d97706;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.card-title i {
  color: #0f65b0;
}

.card-content {
  padding: 24px;
}

/* ===========================================
   FORM ELEMENTS
   =========================================== */

.form-group {
  margin-bottom: 24px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  font-family: "WorkSans-Regular", sans-serif;
  background: white;
  transition: all 0.3s ease;
  margin-top: 8px;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

.form-textarea {
  min-height: 300px;
  resize: vertical;
  line-height: 1.6;
}

.input-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.char-counter {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.char-counter--warning {
  color: #f59e0b;
}

.input-hint {
  font-size: 12px;
  color: #64748b;
}

.input-hint i {
  color: #f59e0b;
}

/* ===========================================
   TOGGLE OPTIONS
   =========================================== */

.toggle-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 12px;
}

.toggle-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  background: white;
  transition: all 0.3s ease;
}

.toggle-option:hover {
  border-color: #0f65b0;
  background: #f8fafc;
}

.toggle-option--active {
  border-color: #0f65b0;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.toggle-option--active .toggle-icon {
  background: #0f65b0;
  color: white;
}

.toggle-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.toggle-icon {
  width: 44px;
  height: 44px;
  background: #f1f5f9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 18px;
  transition: all 0.3s ease;
}

.toggle-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.toggle-desc {
  font-size: 12px;
  color: #64748b;
}

/* ===========================================
   RESIDENTS SELECTOR
   =========================================== */

.residents-selector {
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  margin-top: 12px;
}

.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 200px;
  position: relative;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 14px;
}

.search-input {
  width: 100%;
  padding: 10px 36px 10px 42px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: "WorkSans-Regular", sans-serif;
}

.search-input:focus {
  outline: none;
  border-color: #0f65b0;
}

.search-clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
}

.selector-actions {
  display: flex;
  gap: 8px;
}

.action-link {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  background: #0f65b0;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-link:hover:not(:disabled) {
  background: #1e40af;
}

.action-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-link--danger {
  background: #ef4444;
}

.action-link--danger:hover:not(:disabled) {
  background: #dc2626;
}

.selection-counter {
  padding: 12px 16px;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  font-size: 13px;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
}

.selection-counter--warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #92400e;
}

.residents-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 12px;
}

.residents-loading,
.residents-empty {
  padding: 32px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
}

.resident-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 8px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.resident-item:hover {
  border-color: #0f65b0;
  background: #f8fafc;
}

.resident-item--selected {
  border-color: #0f65b0;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.resident-checkbox {
  display: none;
}

.resident-avatar {
  width: 40px;
  height: 40px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 16px;
}

.resident-item--selected .resident-avatar {
  background: #0f65b0;
  color: white;
}

.resident-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.resident-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.resident-room {
  font-size: 12px;
  color: #64748b;
}

.resident-check {
  width: 24px;
  height: 24px;
  background: #0f65b0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

/* ===========================================
   VARIABLES PANEL
   =========================================== */

.variables-section {
  position: sticky;
  top: 24px;
}

.variables-card .card-content {
  padding: 20px;
}

.variables-hint {
  font-size: 13px;
  color: #92400e;
  margin: 0 0 16px 0;
  padding: 12px;
  background: #fffbeb;
  border-radius: 8px;
}

.variables-list {
  max-height: 500px;
  overflow-y: auto;
}

/* ===========================================
   ERRORS & ACTIONS
   =========================================== */

.errors-section {
  margin-bottom: 24px;
  padding: 16px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 12px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

/* ===========================================
   RESPONSIVE
   =========================================== */

@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .variables-section {
    position: static;
    order: -1;
  }

  .variables-list {
    max-height: 200px;
  }
}

@media (max-width: 768px) {
  .contract-container {
    padding: 0 16px;
  }

  .contract-header {
    padding: 16px 0;
  }

  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .toggle-container {
    grid-template-columns: 1fr;
  }

  .selector-header {
    flex-direction: column;
    align-items: stretch;
  }

  .selector-actions {
    justify-content: center;
  }

  .form-textarea {
    min-height: 200px;
  }
}

@media (max-width: 480px) {
  .toggle-option {
    padding: 14px;
    gap: 12px;
  }

  .toggle-icon {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }
}
</style>